import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import MfaChallengeModal from 'components/layout/MfaChallengeModal';
import {
  extractMfaChallenge,
  setMfaRunner,
  VERIFIED_CHALLENGE_HEADER,
} from 'util/mfaChallenge';

// Generalised in-flow MFA challenge handling.
//
// Any flow can opt in by wrapping its API call with `runWithMfa`:
//
//   const { runWithMfa } = useMfaChallenge();
//   const res = await runWithMfa(opts => createDebit(payload, opts));
//
// The thunk receives an options object that it forwards to the service function
// (which forwards it to the SDK / callApi). On the first attempt no options are
// passed; if the call fails with an MFA challenge, the modal collects + verifies
// an OTP, then the SAME thunk is re-invoked with the `Verified-Challenge` header
// so the request resumes transparently. Non-MFA errors bubble unchanged.
const MfaChallengeContext = createContext(null);

export function MfaChallengeProvider({ children }) {
  const [challenge, setChallenge] = useState(null);
  // Holds the resolve/reject of the promise returned by solveChallenge while the
  // modal is open, so the modal's success/cancel can settle the awaiting caller.
  const pendingRef = useRef(null);

  const solveChallenge = useCallback(activeChallenge => {
    return new Promise((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      setChallenge(activeChallenge);
    });
  }, []);

  const handleSuccess = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setChallenge(null);
    pending?.resolve();
  }, []);

  const handleCancel = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setChallenge(null);
    const err = new Error('mfa_challenge_cancelled');
    err.mfaCancelled = true;
    pending?.reject(err);
  }, []);

  const runWithMfa = useCallback(
    async thunk => {
      try {
        return await thunk();
      } catch (err) {
        const activeChallenge = extractMfaChallenge(err);
        if (!activeChallenge) {
          throw err;
        }
        await solveChallenge(activeChallenge); // resolves once verified, rejects on cancel
        return thunk({
          headers: { [VERIFIED_CHALLENGE_HEADER]: activeChallenge.id },
        });
      }
    },
    [solveChallenge],
  );

  // Expose runWithMfa to non-React callers (util/api.js handlers, page-config
  // onSubmits, class components) via the module bridge. See util/mfaChallenge.js.
  useEffect(() => {
    setMfaRunner(runWithMfa);
    return () => setMfaRunner(null);
  }, [runWithMfa]);

  const value = useMemo(() => ({ runWithMfa, solveChallenge }), [
    runWithMfa,
    solveChallenge,
  ]);

  return (
    <MfaChallengeContext.Provider value={value}>
      {children}
      <MfaChallengeModal
        challenge={challenge}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </MfaChallengeContext.Provider>
  );
}

export function useMfaChallenge() {
  const ctx = useContext(MfaChallengeContext);
  if (!ctx) {
    throw new Error('useMfaChallenge must be used within MfaChallengeProvider');
  }
  return ctx;
}

// HOC for class components: injects `runWithMfa` as a prop.
export function withMfaChallenge(Component) {
  function WithMfaChallenge(props) {
    const { runWithMfa } = useMfaChallenge();
    return <Component {...props} runWithMfa={runWithMfa} />;
  }
  WithMfaChallenge.displayName = `withMfaChallenge(${Component.displayName ||
    Component.name ||
    'Component'})`;
  return WithMfaChallenge;
}
